import { useEffect, useMemo, useState } from "react";
import { usePageMeta } from "@/hooks/usePageMeta";
import { newsList } from "@/mocks/news";
import NewsCard from "@/components/feature/NewsCard";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";

const PAGE_SIZE = 6;
const REFERENCE_DATE = new Date("2025-10-08");

const timeFilters = [
  { label: "最近一周", value: "week" },
  { label: "最近一个月", value: "month" },
  { label: "全部", value: "all" },
];

function withinDays(dateStr: string, days: number) {
  const diff = REFERENCE_DATE.getTime() - new Date(dateStr).getTime();
  return diff <= days * 24 * 60 * 60 * 1000;
}

export default function News() {
  const [range, setRange] = useState("all");
  const [page, setPage] = useState(1);

  usePageMeta({
    title: "影视资讯 - 电影天堂",
    description:
      "电影天堂影视资讯栏目，汇集最新影视动态、票房观察与行业趋势，帮助你及时了解电影圈的大小事。",
    keywords: "影视资讯,电影新闻,票房,行业观察,电影天堂",
  });

  const filtered = useMemo(() => {
    const list = [...newsList].sort((a, b) => (a.publishDate < b.publishDate ? 1 : -1));
    if (range === "week") return list.filter((n) => withinDays(n.publishDate, 7));
    if (range === "month") return list.filter((n) => withinDays(n.publishDate, 30));
    return list;
  }, [range]);

  useEffect(() => {
    setPage(1);
  }, [range]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
      <header className="mb-6">
        <div className="flex items-center gap-3">
          <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
            <i className="ri-newspaper-line text-2xl"></i>
          </span>
          <div>
            <h1 className="font-heading text-2xl font-black text-foreground-950">影视资讯</h1>
            <p className="mt-1 text-sm text-foreground-600">
              掌握最新影视动态、票房与行业观察
            </p>
          </div>
        </div>
      </header>

      {/* 时间筛选 */}
      <div className="mb-6 inline-flex items-center gap-1 rounded-full border border-background-200 bg-background-50 p-1">
        {timeFilters.map((f) => (
          <button
            key={f.value}
            type="button"
            onClick={() => setRange(f.value)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
              range === f.value
                ? "bg-primary-500 text-background-50 dark:text-foreground-950"
                : "text-foreground-600 hover:text-primary-600"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {pageItems.length === 0 ? (
        <EmptyState
          icon="ri-newspaper-line"
          title="该时间段暂无资讯"
          description="换一个时间范围，或返回首页看看其他内容。"
          actionLabel="返回首页"
          actionTo="/"
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {pageItems.map((n) => (
            <NewsCard key={n.id} item={n} />
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="mt-10">
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </div>
      )}
    </div>
  );
}